import { ArrowLeft, Mail, Package, Search } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError } from '../api/http.js';
import {
  MIN_SEARCH_LENGTH,
  useCatalog,
  useLotView,
  useTraceabilitySearch,
} from '../api/queries.js';
import { EmptyState } from '../components/empty-state.js';
import { ErrorState } from '../components/error-state.js';
import { LotSummary } from '../components/lot-summary.js';
import { PageHeader } from '../components/page-header.js';
import { LotMatchList, OrderMatchList } from '../components/search-results.js';
import { searchHref } from '../lib/format.js';

const LOT_PARAM = 'lote';
const QUERY_PARAM = 'q';
const SUGGESTED_KITS = 3;

function SearchHelp() {
  const catalog = useCatalog();
  const suggestions = (catalog.data?.kits ?? []).slice(0, SUGGESTED_KITS).map((kit) => kit.sku);
  return (
    <section
      aria-labelledby="search-help-title"
      className="rounded-card border border-dashed border-border bg-card p-6"
    >
      <h2 id="search-help-title" className="mb-3 font-semibold">
        ¿Qué puedes buscar?
      </h2>
      <ul className="flex flex-col gap-3 text-muted-foreground">
        <li className="flex gap-3">
          <Mail aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
          <span>
            <strong className="text-foreground">El email con que compraste:</strong> ves tus pedidos
            y los lotes de los kits que recibiste.
          </span>
        </li>
        <li className="flex gap-3">
          <Package aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
          <span>
            <strong className="text-foreground">El código de un producto</strong> (como aparece en
            el catálogo): ves todos sus lotes, del más reciente al más antiguo.
          </span>
        </li>
        <li className="flex gap-3">
          <Search aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
          <span>
            <strong className="text-foreground">Un ID de lote</strong> (empieza con <code>L-</code>
            ): ves de qué insumos se hizo, en qué se usó y a quién se entregó.
          </span>
        </li>
      </ul>
      {suggestions.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Prueba con:</span>
          {suggestions.map((sku) => (
            <Link
              key={sku}
              to={searchHref(sku)}
              className="inline-flex min-h-11 items-center rounded-full border border-border px-3 text-sm font-medium text-accent hover:border-accent"
            >
              {sku}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function SearchResults({ query }: { query: string }) {
  const [, setSearchParams] = useSearchParams();
  const search = useTraceabilitySearch(query);
  const exactLot = search.data?.kind === 'lot' ? search.data.lotId : undefined;

  useEffect(() => {
    if (exactLot !== undefined) setSearchParams({ [LOT_PARAM]: exactLot }, { replace: true });
  }, [exactLot, setSearchParams]);

  if (query.length < MIN_SEARCH_LENGTH)
    return <EmptyState message="Escribe al menos 3 caracteres." />;
  if (search.isPending) return <p aria-live="polite">Buscando «{query}»…</p>;
  if (search.isError)
    return (
      <ErrorState message="No pudimos hacer la búsqueda." onRetry={() => void search.refetch()} />
    );
  if (search.data.kind === 'lots' && search.data.lots.length > 0)
    return <LotMatchList lots={search.data.lots} query={query} />;
  if (search.data.kind === 'orders' && search.data.orders.length > 0)
    return <OrderMatchList orders={search.data.orders} query={query} />;
  if (search.data.kind === 'lot') return null;
  return (
    <div className="flex flex-col gap-4">
      <EmptyState
        message={
          search.data.kind === 'orders'
            ? `No hay pedidos con el email ${query}. Revisa que sea el mismo que usaste al pagar.`
            : `No encontramos lotes ni productos que coincidan con «${query}».`
        }
      />
      <SearchHelp />
    </div>
  );
}

function LotDetail({ lotId, backQuery }: { lotId: string; backQuery: string }) {
  const lot = useLotView(lotId);
  return (
    <div className="flex flex-col gap-4">
      {backQuery !== '' && (
        <Link
          to={searchHref(backQuery)}
          className="inline-flex min-h-11 items-center gap-1 self-start font-medium text-accent"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Volver a los resultados de «{backQuery}»
        </Link>
      )}
      {lot.isPending && <p aria-live="polite">Consultando el lote {lotId}…</p>}
      {lot.isError &&
        (lot.error instanceof ApiError && lot.error.status === 404 ? (
          <EmptyState message={`El lote ${lotId} no está en nuestros registros.`} />
        ) : (
          <ErrorState message="No pudimos consultar el lote." onRetry={() => void lot.refetch()} />
        ))}
      {lot.isSuccess && <LotSummary lot={lot.data} />}
    </div>
  );
}

export function TraceabilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLot = searchParams.get(LOT_PARAM) ?? '';
  const currentQuery = searchParams.get(QUERY_PARAM) ?? '';
  const [draft, setDraft] = useState(currentQuery || currentLot);

  useEffect(() => {
    setDraft(currentQuery || currentLot);
  }, [currentQuery, currentLot]);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const query = draft.trim();
    setSearchParams(query === '' ? {} : { [QUERY_PARAM]: query });
  }

  return (
    <>
      <PageHeader
        title="Trazabilidad"
        description="Sigue cualquier kit hasta los insumos de Farma Central con que se hizo, o encuentra lo que compraste con tu email."
      />
      <form onSubmit={submit} className="mb-8 flex flex-wrap items-end gap-3" role="search">
        <div className="flex min-w-64 flex-1 flex-col gap-1">
          <label htmlFor="trace-query" className="font-medium">
            Email, código de producto o ID de lote
          </label>
          <input
            id="trace-query"
            name={QUERY_PARAM}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            placeholder="tu@email.cl · KIT-RESP-ADULTO · L-KIT-…"
            autoComplete="off"
            className="min-h-11 rounded-control border border-border bg-card px-3"
          />
        </div>
        <button
          type="submit"
          className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-control bg-accent px-5 font-medium text-on-accent transition-opacity duration-150 hover:opacity-90"
        >
          <Search aria-hidden="true" className="size-4" />
          Buscar
        </button>
      </form>
      {currentLot !== '' && <LotDetail lotId={currentLot} backQuery={currentQuery} />}
      {currentLot === '' && currentQuery !== '' && <SearchResults query={currentQuery} />}
      {currentLot === '' && currentQuery === '' && <SearchHelp />}
    </>
  );
}
